import React from 'react';
import { useSolarStore } from '../../store/useSolarStore';
import { ShieldCheck, Zap, BatteryCharging, Sun, Check, ArrowRight, Award } from 'lucide-react';

export const PackageCards: React.FC = () => {
  const { selectPreset, setIsLeadModalOpen, presets } = useSolarStore();

  const handleSelectAndQuote = (preset: any) => {
    selectPreset(preset);
    setIsLeadModalOpen(true, `WhatsApp Quote Request: ${preset.name} (${preset.capacityKva} kVA)`);
  };

  return (
    <div className="space-y-8 my-10">
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold uppercase tracking-wider">
          <Award className="w-4 h-4" /> Tier-1 Nigerian Solar Packages
        </div>
        <h2 className="text-3xl sm:text-4xl font-black text-white">Engineered for Nigerian Power Realities</h2>
        <p className="text-slate-400 text-sm">
          Complete turnkey hybrid solar packages featuring Grade-A LiFePO4 lithium batteries, high-efficiency monocrystalline panels, and pure sine wave inverters.
        </p>

        {/* Brand Trust Badges */}
        <div className="flex flex-wrap justify-center items-center gap-3 pt-3">
          {['Felicity Solar', 'Must', 'Growatt', 'Deye', 'Canadian Solar', 'Tubular/LiFePO4'].map((brand, idx) => (
            <span key={idx} className="px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-300 text-xs font-bold">
              ✓ {brand}
            </span>
          ))}
        </div>
      </div>

      {/* Package Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
        {presets.map((preset, index) => {
          const isPopular = index === 1; // 3.5kVA Standard is popular

          return (
            <div
              key={preset.id}
              className={`bg-slate-900 rounded-3xl p-6 sm:p-8 border flex flex-col justify-between transition-all duration-300 relative ${
                isPopular
                  ? 'border-emerald-500 shadow-2xl shadow-emerald-500/15 scale-105 bg-gradient-to-b from-slate-900 to-slate-950'
                  : 'border-slate-800 hover:border-slate-700'
              }`}
            >
              {isPopular && (
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] font-black uppercase tracking-widest px-4 py-1.5 rounded-full shadow-lg">
                  Most Popular in Nigeria
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <div className="flex justify-between items-start">
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">
                      {preset.capacityKva} kVA Hybrid System
                    </span>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-slate-800 text-slate-300 font-medium">
                      {preset.batteryCapacityKwh} kWh LiFePO4
                    </span>
                  </div>
                  <h3 className="text-2xl font-black text-white mt-2">{preset.name}</h3>
                  <p className="text-xs text-slate-400 mt-1">{preset.recommendedFor}</p>
                </div>

                <div className="bg-slate-950 p-4 rounded-2xl border border-slate-800/80 space-y-2">
                  <div className="text-xs font-bold text-slate-300">Package Specification:</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="text-slate-400">Solar Panels: <span className="text-white font-bold">{preset.solarPanelCapacityKw} kW</span></div>
                    <div className="text-slate-400">Inverter: <span className="text-white font-bold">{preset.capacityKva} kVA</span></div>
                    <div className="text-slate-400">Battery: <span className="text-white font-bold">{preset.batteryCapacityKwh} kWh</span></div>
                    <div className="text-slate-400">Warranty: <span className="text-emerald-400 font-bold">5-10 Years</span></div>
                  </div>
                </div>

                <div className="space-y-2.5">
                  <div className="text-xs font-bold text-slate-300 uppercase tracking-wide">Supported Appliances:</div>
                  <ul className="space-y-2">
                    {preset.typicalAppliances.map((app, i) => (
                      <li key={i} className="text-xs text-slate-300 flex items-start gap-2">
                        <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                        <span>{app}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-slate-800 space-y-4">
                <div>
                  <span className="text-[10px] text-slate-400 block uppercase font-bold tracking-wider">Estimated Total Package Price</span>
                  <div className="text-2xl font-black text-emerald-400">₦{preset.priceNgn.toLocaleString()}</div>
                </div>

                <button
                  onClick={() => handleSelectAndQuote(preset)}
                  className={`w-full font-bold py-3.5 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 text-xs ${
                    isPopular
                      ? 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-emerald-500/25'
                      : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                  }`}
                >
                  Select System & Get WhatsApp Quote
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
